<script lang="ts">
  // Trade negotiation window. Two offers side by side; you add cards from your
  // collection and lock when satisfied. Editing either offer resets the other side's
  // lock (server-enforced) so no one can bait-and-switch. Both lock → both confirm →
  // the server runs the atomic swap. Everything shown here is the server's authoritative
  // TradeState; we only propose changes.
  import { fade, scale } from "svelte/transition";
  import { expoOut } from "svelte/easing";
  import { trade } from "../stores/trade.svelte.ts";
  import { send, listen } from "../stores/net.svelte.ts";
  import DataWindow from "./ui/DataWindow.svelte";
  import { cardImage } from "../../data.ts";
  import { cardsByIds } from "../cards.ts";
  import { cosmeticName, cosmeticRarity } from "../cosmetics.ts";
  import { GACHA_TEXT } from "../cosmetic-rarity.ts";
  import { foilColor, foilLabel } from "../rarity.ts";
  import type { InventoryItem, TradeOfferCard, TradeOfferCosmetic, TradeOfferFoil, OwnedCosmetic, CosmeticKind } from "@ygoevo/world-protocol";
  import { draggableDialog } from "../actions/draggableDialog.ts";

  let { onClose }: { onClose: () => void } = $props();

  let inv = $state<InventoryItem[]>([]);
  let cosInv = $state<OwnedCosmetic[]>([]);
  let names = $state<Record<number, string>>({});
  let q = $state("");
  let pick = $state<"cards" | "foils" | "cosmetics">("cards");

  const you = $derived(trade.state?.you);
  const them = $derived(trade.state?.them);
  const myCards = $derived(you?.cards ?? []);
  const myCos = $derived(you?.cosmetics ?? []);
  const myFoils = $derived(you?.foils ?? []);
  const locked = $derived(!!you?.locked);
  const bothLocked = $derived(!!you?.locked && !!them?.locked);
  // Why is Confirm disabled? (one-sided "free" gifts are fine, the empty side still has to lock.)
  const confirmHint = $derived(
    you?.confirmed ? `waiting for ${trade.withName} to confirm…`
    : !locked ? "lock your offer to continue"
    : !them?.locked ? `waiting for ${trade.withName} to lock`
    : "both locked, confirm to trade",
  );
  const offeredQty = (cid: number): number => myCards.find((c) => c.cardId === cid)?.qty ?? 0;
  const offeredCosQty = (kind: CosmeticKind, itemId: string): number => myCos.find((c) => c.kind === kind && c.itemId === itemId)?.qty ?? 0;
  const offeredFoilQty = (cid: number, grade: number): number => myFoils.find((f) => f.cardId === cid && f.grade === grade)?.qty ?? 0;

  const plainItems = $derived(inv.filter((i) => !i.foil));
  const foilItems = $derived(inv.filter((i) => !!i.foil));
  // Tradeable copies only, soul-bound starter-deck cards (qty − bound) can't be offered.
  const ownedMap = $derived(Object.fromEntries(plainItems.map((i) => [i.cardId, i.qty - (i.bound ?? 0)])) as Record<number, number>);

  // Default sleeve/disk/outfit are always-owned freebies, not tradeable.
  const UNTRADEABLE = new Set(["default", "dd_01_c01", "dc_01_c01", "dc_01_c02", "dc_01_c03", "dc_01_c04"]);
  const tradeableCos = $derived(cosInv.filter((c) => !UNTRADEABLE.has(c.itemId)));

  const matchesQ = (cid: number) => !q || (names[cid] ?? "").toLowerCase().includes(q.toLowerCase()) || String(cid).includes(q);
  const visibleInv = $derived(plainItems.filter((i) => i.qty - (i.bound ?? 0) > 0).filter((i) => matchesQ(i.cardId)).slice(0, 120));
  const visibleFoils = $derived(foilItems.filter((i) => matchesQ(i.cardId)).slice(0, 120));

  listen("Inventory", (m) => {
    inv = m.items; // plain AND foil rows, both are tradeable (foils have their own grade)
    const miss = inv.map((i) => i.cardId).filter((c) => !(c in names));
    if (miss.length)
      cardsByIds(miss)
        .then((cs) => { for (const c of cs) names[c.id] = c.name; })
        .catch(() => {});
  });
  listen("CosmeticInventory", (m) => (cosInv = m.items));
  send({ t: "InventoryGet" });
  send({ t: "CosmeticInventoryGet" });

  const pushOffer = (cards: TradeOfferCard[], cosmetics: TradeOfferCosmetic[], foils: TradeOfferFoil[]) =>
    send({ t: "TradeSetOffer", cards, cosmetics, foils });
  function add(cid: number) {
    if (locked) return;
    const cur = offeredQty(cid);
    if (cur >= Math.min(ownedMap[cid] ?? 0, 3)) return;
    pushOffer([...myCards.filter((c) => c.cardId !== cid), { cardId: cid, qty: cur + 1 }], myCos, myFoils);
  }
  function remove(cid: number) {
    if (locked) return;
    const cur = offeredQty(cid);
    const next = cur <= 1 ? myCards.filter((c) => c.cardId !== cid) : [...myCards.filter((c) => c.cardId !== cid), { cardId: cid, qty: cur - 1 }];
    pushOffer(next, myCos, myFoils);
  }
  function addCos(kind: CosmeticKind, itemId: string) {
    if (locked) return;
    const owned = cosInv.find((c) => c.kind === kind && c.itemId === itemId)?.qty ?? 0;
    const cur = offeredCosQty(kind, itemId);
    if (cur >= owned) return;
    pushOffer(myCards, [...myCos.filter((c) => !(c.kind === kind && c.itemId === itemId)), { kind, itemId, qty: cur + 1 }], myFoils);
  }
  function removeCos(kind: CosmeticKind, itemId: string) {
    if (locked) return;
    const cur = offeredCosQty(kind, itemId);
    const rest = myCos.filter((c) => !(c.kind === kind && c.itemId === itemId));
    pushOffer(myCards, cur <= 1 ? rest : [...rest, { kind, itemId, qty: cur - 1 }], myFoils);
  }
  function addFoil(cid: number, grade: number) {
    if (locked) return;
    const owned = foilItems.find((i) => i.cardId === cid && i.foil === grade)?.qty ?? 0;
    const cur = offeredFoilQty(cid, grade);
    if (cur >= owned) return;
    pushOffer(myCards, myCos, [...myFoils.filter((f) => !(f.cardId === cid && f.grade === grade)), { cardId: cid, grade, qty: cur + 1 }]);
  }
  function removeFoil(cid: number, grade: number) {
    if (locked) return;
    const cur = offeredFoilQty(cid, grade);
    const rest = myFoils.filter((f) => !(f.cardId === cid && f.grade === grade));
    pushOffer(myCards, myCos, cur <= 1 ? rest : [...rest, { cardId: cid, grade, qty: cur - 1 }]);
  }
  const toggleLock = () => send({ t: "TradeLock", locked: !locked });
  const doConfirm = () => send({ t: "TradeConfirm" });
  const name = (cid: number) => names[cid] ?? `#${cid}`;
</script>

{#snippet offerTile(cid: number, qty: number, mineSide: boolean)}
  <button
    class="relative size-[52px] shrink-0 overflow-hidden chamfer ring-1 ring-cyan/25 {mineSide && !locked ? 'cursor-pointer hover:ring-magenta/70' : 'cursor-default'}"
    style="--c:5px"
    onclick={() => mineSide && remove(cid)}
    title={mineSide && !locked ? `${name(cid)}, click to remove` : name(cid)}
  >
    <img src={cardImage(cid)} alt={name(cid)} class="size-full object-cover" />
    {#if qty > 1}<span class="absolute bottom-0 right-0 bg-void/85 px-1 font-display text-[10px] font-bold text-gold">×{qty}</span>{/if}
  </button>
{/snippet}

{#snippet cosChip(kind: CosmeticKind, itemId: string, qty: number, mineSide: boolean)}
  <button
    class="flex h-[52px] shrink-0 items-center gap-1.5 chamfer bg-void/40 px-2 ring-1 ring-cyan/25 {mineSide && !locked ? 'cursor-pointer hover:ring-magenta/70' : 'cursor-default'}"
    style="--c:5px"
    onclick={() => mineSide && removeCos(kind, itemId)}
    title={mineSide && !locked ? `${cosmeticName(kind, itemId)}, click to remove` : cosmeticName(kind, itemId)}
  >
    <span class="flex max-w-[104px] flex-col items-start leading-tight">
      <span class="max-w-full truncate font-display text-[0.62rem] font-semibold text-ink">{cosmeticName(kind, itemId)}</span>
      <span class="font-display text-[0.5rem] uppercase tracking-wide {GACHA_TEXT[cosmeticRarity(kind, itemId)]}">{kind}</span>
    </span>
    {#if qty > 1}<span class="font-display text-[10px] font-bold text-gold">×{qty}</span>{/if}
  </button>
{/snippet}

{#snippet foilTile(cid: number, grade: number, qty: number, mineSide: boolean)}
  <button
    class="relative size-[52px] shrink-0 overflow-hidden chamfer ring-2 {mineSide && !locked ? 'cursor-pointer hover:ring-magenta/70' : 'cursor-default'}"
    style="--c:5px;--tw-ring-color:{foilColor(grade)}"
    onclick={() => mineSide && removeFoil(cid, grade)}
    title={mineSide && !locked ? `${name(cid)} (${foilLabel(grade)}), click to remove` : `${name(cid)} (${foilLabel(grade)})`}
  >
    <img src={cardImage(cid)} alt={name(cid)} class="size-full object-cover" />
    <span class="absolute left-0 top-0 px-1 font-display text-[9px] font-bold" style="color:{foilColor(grade)};background:rgba(0,0,0,0.7)">✦{grade}</span>
    {#if qty > 1}<span class="absolute bottom-0 right-0 bg-void/85 px-1 font-display text-[10px] font-bold text-gold">×{qty}</span>{/if}
  </button>
{/snippet}

<div class="mobile-dialog-host fixed inset-0 z-[55] grid place-items-center p-4" transition:fade={{ duration: 160 }}>
  <button class="modal-scrim absolute inset-0 bg-void/50" onclick={onClose} aria-label="Close"></button>
  <div
    class="mobile-full-dialog relative w-[min(760px,96vw)]"
    role="dialog"
    aria-modal="true"
    aria-label="Trade"
    use:draggableDialog
    transition:scale={{ start: 0.96, opacity: 0, duration: 220, easing: expoOut }}
  >
    <DataWindow c={18} fill class="mobile-dialog-panel">
      <!-- header -->
      <div data-dialog-drag-handle class="dialog-drag-handle flex items-center justify-between border-b border-cyan-dim/40 bg-surface-2/70 px-5 py-2.5">
        <span class="flex items-center gap-2">
          <span class="size-1.5 rounded-full bg-gold animate-pulse-soft" style="filter:drop-shadow(0 0 5px var(--color-gold))"></span>
          <span class="font-display text-[0.72rem] font-semibold uppercase tracking-[0.26em] text-gold">Trade · {trade.withName}</span>
        </span>
        <button onclick={onClose} class="text-ink-dim transition-colors hover:text-magenta" aria-label="Cancel trade">✕</button>
      </div>

      <div class="mobile-dialog-scroll min-h-0 flex-1 overflow-y-auto scroll-cyber">
      <!-- two offers -->
      <div class="grid grid-cols-2 gap-px bg-cyan-dim/15">
        {#each [{ side: you, mine: true, label: "Your offer" }, { side: them, mine: false, label: `${trade.withName}'s offer` }] as col (col.label)}
          <div class="bg-surface/60 p-3">
            <div class="mb-2 flex items-center justify-between">
              <span class="font-display text-[0.6rem] uppercase tracking-[0.18em] {col.mine ? 'text-cyan' : 'text-magenta'}">{col.label}</span>
              <span class="font-display text-[0.58rem] uppercase tracking-widest {col.side?.locked ? 'text-lime' : 'text-ink-dim/60'}">
                {col.side?.confirmed ? "✓ confirmed" : col.side?.locked ? "● locked" : "editing…"}
              </span>
            </div>
            <div class="flex min-h-[58px] flex-wrap content-start gap-1.5 chamfer bg-void-2/40 p-2" style="--c:8px">
              {#each col.side?.cards ?? [] as c (c.cardId)}
                {@render offerTile(c.cardId, c.qty, col.mine)}
              {/each}
              {#each col.side?.foils ?? [] as c (c.cardId + "f" + c.grade)}
                {@render foilTile(c.cardId, c.grade, c.qty, col.mine)}
              {/each}
              {#each col.side?.cosmetics ?? [] as c (c.kind + c.itemId)}
                {@render cosChip(c.kind, c.itemId, c.qty, col.mine)}
              {/each}
              {#if !(col.side?.cards?.length) && !(col.side?.foils?.length) && !(col.side?.cosmetics?.length)}
                <span class="grid h-[52px] w-full place-items-center font-body text-xs text-ink-dim/50">nothing offered</span>
              {/if}
            </div>
          </div>
        {/each}
      </div>

      <!-- your collection picker (cards or cosmetics) -->
      <div class="border-t border-cyan-dim/20 p-3">
        <div class="mb-2 flex items-center justify-between gap-3">
          <div class="flex items-center gap-1.5">
            <button onclick={() => (pick = "cards")} class="chamfer-btn px-2.5 py-1 font-display text-[0.6rem] font-semibold uppercase tracking-[0.14em] transition-colors {pick === 'cards' ? 'bg-cyan/15 text-cyan' : 'text-ink-dim hover:text-ink'}" style="--c:5px">Cards</button>
            <button onclick={() => (pick = "foils")} class="chamfer-btn px-2.5 py-1 font-display text-[0.6rem] font-semibold uppercase tracking-[0.14em] transition-colors {pick === 'foils' ? 'bg-cyan/15 text-cyan' : 'text-ink-dim hover:text-ink'}" style="--c:5px">Foils</button>
            <button onclick={() => (pick = "cosmetics")} class="chamfer-btn px-2.5 py-1 font-display text-[0.6rem] font-semibold uppercase tracking-[0.14em] transition-colors {pick === 'cosmetics' ? 'bg-cyan/15 text-cyan' : 'text-ink-dim hover:text-ink'}" style="--c:5px">Cosmetics</button>
            <span class="ml-1 font-display text-[0.55rem] uppercase tracking-widest text-ink-dim/70">{locked ? "(locked)" : "click to add"}</span>
          </div>
          {#if pick === "cards" || pick === "foils"}
            <input
              bind:value={q}
              placeholder="Search…"
              class="chamfer-btn w-40 bg-void-2/70 px-2.5 py-1 font-body text-xs text-ink outline-none ring-1 ring-cyan/20 placeholder:text-ink-dim/50 focus:ring-cyan/60"
              style="--c:6px"
            />
          {/if}
        </div>
        {#if pick === "cards"}
          <div class="grid max-h-[210px] grid-cols-[repeat(auto-fill,minmax(48px,1fr))] gap-1.5 overflow-y-auto {locked ? 'pointer-events-none opacity-40' : ''}">
            {#each visibleInv as it (it.cardId)}
              {@const remaining = (ownedMap[it.cardId] ?? 0) - offeredQty(it.cardId)}
              <button
                class="relative aspect-[59/86] overflow-hidden chamfer ring-1 ring-cyan/15 transition hover:ring-cyan/70 disabled:opacity-30"
                style="--c:5px"
                disabled={remaining <= 0 || offeredQty(it.cardId) >= 3}
                onclick={() => add(it.cardId)}
                title={name(it.cardId)}
              >
                <img src={cardImage(it.cardId)} alt={name(it.cardId)} class="size-full object-cover" />
                <span class="absolute bottom-0 right-0 bg-void/85 px-1 font-display text-[9px] font-bold text-cyan">{remaining}</span>
              </button>
            {/each}
          </div>
        {:else if pick === "foils"}
          <div class="grid max-h-[210px] grid-cols-[repeat(auto-fill,minmax(48px,1fr))] gap-1.5 overflow-y-auto {locked ? 'pointer-events-none opacity-40' : ''}">
            {#if visibleFoils.length === 0}
              <span class="col-span-full grid place-items-center py-6 text-center font-body text-xs text-ink-dim/60">No foil cards, pull holos from packs to trade them.</span>
            {/if}
            {#each visibleFoils as it (it.cardId + "f" + it.foil)}
              {@const remaining = it.qty - offeredFoilQty(it.cardId, it.foil!)}
              <button
                class="relative aspect-[59/86] overflow-hidden chamfer ring-2 transition hover:brightness-125 disabled:opacity-30"
                style="--c:5px;--tw-ring-color:{foilColor(it.foil!)}"
                disabled={remaining <= 0}
                onclick={() => addFoil(it.cardId, it.foil!)}
                title="{name(it.cardId)} ({foilLabel(it.foil!)})"
              >
                <img src={cardImage(it.cardId)} alt={name(it.cardId)} class="size-full object-cover" />
                <span class="absolute left-0 top-0 px-1 font-display text-[9px] font-bold" style="color:{foilColor(it.foil!)};background:rgba(0,0,0,0.7)">✦{it.foil}</span>
                <span class="absolute bottom-0 right-0 bg-void/85 px-1 font-display text-[9px] font-bold text-cyan">{remaining}</span>
              </button>
            {/each}
          </div>
        {:else}
          <div class="grid max-h-[210px] grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-1.5 overflow-y-auto {locked ? 'pointer-events-none opacity-40' : ''}">
            {#if tradeableCos.length === 0}
              <span class="col-span-full grid place-items-center py-6 text-center font-body text-xs text-ink-dim/60">No tradeable cosmetics, open boxes to collect duplicates you can trade.</span>
            {/if}
            {#each tradeableCos as c (c.kind + c.itemId)}
              {@const remaining = c.qty - offeredCosQty(c.kind, c.itemId)}
              <button
                class="flex items-center justify-between gap-1.5 chamfer border border-cyan-dim/20 bg-void-2/40 px-2.5 py-2 text-left transition-colors hover:border-cyan/50 disabled:opacity-30"
                style="--c:6px"
                disabled={remaining <= 0}
                onclick={() => addCos(c.kind, c.itemId)}
                title={cosmeticName(c.kind, c.itemId)}
              >
                <span class="flex min-w-0 flex-col leading-tight">
                  <span class="truncate font-display text-[0.68rem] font-semibold text-ink">{cosmeticName(c.kind, c.itemId)}</span>
                  <span class="font-display text-[0.5rem] uppercase tracking-wider {GACHA_TEXT[cosmeticRarity(c.kind, c.itemId)]}">{c.kind}</span>
                </span>
                <span class="shrink-0 chamfer-btn bg-void/80 px-1.5 py-0.5 font-display text-[0.6rem] font-bold text-cyan" style="--c:3px">×{remaining}</span>
              </button>
            {/each}
          </div>
        {/if}
      </div>

      </div>

      <!-- footer -->
      <div class="flex items-center justify-between gap-3 border-t border-cyan-dim/25 bg-surface-2/40 px-4 py-3">
        <button onclick={onClose} class="chamfer-btn bg-void-2/50 px-4 py-2 font-display text-xs font-semibold uppercase tracking-widest text-ink-dim transition-colors hover:text-magenta" style="--c:7px">Cancel</button>
        <span class="flex-1 px-2 text-center font-display text-[0.58rem] uppercase tracking-widest {bothLocked && !you?.confirmed ? 'text-lime' : 'text-ink-dim/70'}">{confirmHint}</span>
        <div class="flex items-center gap-2.5">
          <button
            onclick={toggleLock}
            class="chamfer-btn px-5 py-2 font-display text-xs font-bold uppercase tracking-widest transition-colors {locked ? 'bg-lime/15 text-lime' : 'bg-cyan/15 text-cyan hover:bg-cyan/25'}"
            style="--c:7px"
          >
            {locked ? "Unlock" : "Lock offer"}
          </button>
          <button
            onclick={doConfirm}
            disabled={!bothLocked || you?.confirmed}
            class="chamfer-btn bg-gold/15 px-6 py-2 font-display text-xs font-bold uppercase tracking-widest text-gold transition-colors hover:bg-gold/25 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-gold/15"
            style="--c:7px"
          >
            {you?.confirmed ? "Waiting…" : "Confirm"}
          </button>
        </div>
      </div>
    </DataWindow>
  </div>
</div>
